import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild, ElementRef, Output, EventEmitter, Injector } from "@angular/core";
import { Subscription } from "rxjs/internal/Subscription";
import { Observable, takeUntil } from "rxjs";
import { AbstractControl, FormControl, Validators } from "@angular/forms";
import { SCFPrivileges, SCFWorkRequired, ScfV2Service } from "../../scf-v2.service";
import { EnvironmentService } from "src/app/services/environment/environment.service";
import { TesterToolsService } from "src/app/services/environment/tester-tools.service";
import { MatAutocomplete, MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";
import { MatChipInputEvent } from "@angular/material/chips";
import moment from "moment";
import { CdkDragDrop, CdkDragMove, CdkDropList, CdkDropListGroup, moveItemInArray } from "@angular/cdk/drag-drop";
import { ViewportRuler } from "@angular/cdk/scrolling";
import { WorkRequired, ResourceType, NotificationType, FormType, OlogNotificationType, StatusResource, Roles, FormStatusEnum } from "src/app/common/enumerations/enumerations";
import { BeamlineRequiringApproval } from "src/app/components/catalogs/bratoi/models/beamline-requiring-approval";
import { utils } from "src/app/modules/libs/utils";
import { Document, DocumentCategory, RelatedDocument } from "src/app/services/documents/documents";
import { NotificationMessage } from "src/app/components/catalogs/notifications/services/notification.models";
import { ResourceRelation, Resource, Location, ShieldingGroup, ResourceGroup } from "src/app/components/catalogs/beamline-catalog/resource/resources";
import { TOCA } from "src/app/components/catalogs/beamline-catalog/resource/toca";
import { SCFMaster, SCFAmendment } from "../../scf";
import { BaseComponent } from "src/app/common/base/base.component";
import { CommentsBoxComponent } from "src/app/controls/comments-box/comments-box.component";
import { RelatedLink } from "src/app/common/models/related-link";
import { NotificationService } from "src/app/components/catalogs/notifications/services/notification.service";
import { OLogService } from "src/app/components/olog/olog.service";
import { NotificationComponent } from "src/app/controls/notifications/notification.component";
import { PartialOlogComponent } from "src/app/components/olog/partial-olog/partial-olog.component";
import { AddRelatedLinkComponent } from "src/app/controls/add-related-link/add-related-link.component";
import { User } from "src/app/components/catalogs/user-catalog/services/user";
import { RelatedLinkBase } from "src/app/components/catalogs/navigation-links/link/related-link-base";
import { PrivilegeEnum } from "src/app/common/enumerations/privilege-enum";
import { FormViewState } from "src/app/common/models/form-view-state";
import { Servers } from "src/app/common/enumerations/servers-enum";
import { BratoiService } from "src/app/components/catalogs/bratoi/bratoi.service";

@Component({
  selector: 'scf-v2-main-s1',
  templateUrl: './scf-v2-main-s1.component.html',
  styleUrls: ['./scf-v2-main-s1.component.scss'],
})
export class ScfV2MainS1Component
  extends BaseComponent
  implements OnInit, OnDestroy, OnChanges {
  @Input() scfMaster?: SCFMaster | null;
  @Input() disabled? = true;
  @Input() privileges?: SCFPrivileges;
  @Input() status?: FormViewState;

  @Output() changed = new EventEmitter<SCFMaster>();
  @Output() workRequiredChanged = new EventEmitter<any>();
  @Output() locationAdded = new EventEmitter<number>();
  @Output() customRestrictionRequested = new EventEmitter<string>();
  @Output() resetRequested = new EventEmitter<void>();

  @ViewChild('locationInput') locationInput!: ElementRef<HTMLInputElement>;
  @ViewChild('shieldingInput') shieldingInput!: ElementRef<HTMLInputElement>;
  @ViewChild('autoLoc') matAutocompleteLoc!: MatAutocomplete;
  @ViewChild('autoShl') matAutocompleteShl!: MatAutocomplete;
  @ViewChild('relatedLinksInput')
  relatedLinksInput!: ElementRef<HTMLInputElement>;

  @ViewChild(CommentsBoxComponent)
  descriptionComponent?: CommentsBoxComponent;
  @ViewChild('chipListLoc')
  chipListLoc!: { errorState: boolean; };
  @ViewChild('chipListShl')
  chipListShl!: { errorState: boolean; };

  currentSCFMaster!: SCFMaster;

  addOnBlur = true;
  descriptionHasError = false;

  /** Cached result of formValid(); read by isValid() for template bindings. */
  formValidValue = false;

  disableControls = true;

  locations: Location[] | null = [];
  allLocations!: Location[];
  filteredLocations!: Location[];
  locationGroups!: ResourceGroup[];
  shieldingGroups!: ShieldingGroup[];

  allShieldings: ResourceRelation[] = [];
  resources!: Resource[];
  resources$!: Observable<Resource[]>;
  resourcesSubs!: Subscription;

  resourceRelations!: ResourceRelation[];
  resourceRelations$!: Observable<ResourceRelation[]>;
  resourceRelationsSubs!: Subscription;

  requesters!: User[];

  bratois!: BeamlineRequiringApproval[];
  bratois$!: Observable<BeamlineRequiringApproval[]>;
  bratoisSubs!: Subscription;

  shieldings: ResourceRelation[] = [];
  filteredShieldings: ResourceRelation[] = [];

  documents?: RelatedDocument[] | null = [];

  relatedLinks: RelatedLinkBase[] = [];

  notificationMessage!: NotificationMessage | null;
  notificationButtonLabel!: string;
  notificationButtonDisabled!: boolean;

  tocas!: TOCA[];
  tocas$!: Observable<TOCA[]>;
  tocasSubs!: Subscription;

  today = new Date();

  scfAmendment?: SCFAmendment;

  descriptionCtrlText!: string | null;
  workRequired = WorkRequired;

  /** Running count of auto-fill runs, used to number appended description lines. */
  private autoFillEntryCount = 0;

  mentions: any[] = [];
  unapprovedTocaMentionIds: number[] = [];
  selectedMentions?: any[];

  public get checkAccRFCtrl(): AbstractControl | null {
    return this.formGroup?.get('checkAccRFCtrl');
  }
  public get locationsCtrl(): AbstractControl | null {
    return this.formGroup?.get('locationsCtrl');
  }
  public get shieldingsCtrl(): AbstractControl | null {
    return this.formGroup?.get('shieldingsCtrl');
  }
  public get checkBlShCtrl(): AbstractControl | null {
    return this.formGroup?.get('checkBlShCtrl');
  }
  public get checkTocaMovCtrl(): AbstractControl | null {
    return this.formGroup?.get('checkTocaMovCtrl');
  }
  public get checkPermBlCtrl(): AbstractControl | null {
    return this.formGroup?.get('checkPermBlCtrl');
  }
  public get checkPermAccCtrl(): AbstractControl | null {
    return this.formGroup?.get('checkPermAccCtrl');
  }
  public get inputWorkHiddenCtrl(): AbstractControl | null {
    return this.formGroup?.get('inputWorkHiddenCtrl');
  }
  public get inputNotificationsHiddenCtrl(): AbstractControl | null {
    return this.formGroup?.get('inputNotificationsHiddenCtrl');
  }
  public get relatedLinksCtrl(): AbstractControl | null {
    return this.formGroup?.get('relatedLinksCtrl');
  }
  public get requesterCtrl(): AbstractControl | null {
    return this.formGroup?.get('requesterCtrl');
  }
  public get estimatedInitCtrl(): AbstractControl | null {
    return this.formGroup?.get('estimatedInitCtrl');
  }
  public get estimatedEndCtrl(): AbstractControl | null {
    return this.formGroup?.get('estimatedEndCtrl');
  }
  public get notiCheckHPCtrl(): AbstractControl | null {
    return this.formGroup?.get('notiCheckHPCtrl');
  }
  public get notiCheckBLCtrl(): AbstractControl | null {
    return this.formGroup?.get('notiCheckBLCtrl');
  }
  public get notiCheckBRCCtrl(): AbstractControl | null {
    return this.formGroup?.get('notiCheckBRCCtrl');
  }
  public get notiCheckRFCtrl(): AbstractControl | null {
    return this.formGroup?.get('notiCheckRFCtrl');
  }
  public get notiCheckSACtrl(): AbstractControl | null {
    return this.formGroup?.get('notiCheckSACtrl');
  }
  public get notiCheckARCtrl(): AbstractControl | null {
    return this.formGroup?.get('notiCheckARCtrl');
  }
  public get notiCheckRPCtrl(): AbstractControl | null {
    return this.formGroup?.get('notiCheckRPCtrl');
  }
  descriptionCtrl = new FormControl();

  @ViewChild(CdkDropListGroup)
  listGroup!: CdkDropListGroup<CdkDropList>;
  @ViewChild(CdkDropList)
  placeholder!: CdkDropList;

  public target: any;
  public targetParent: any;
  public targetIndex!: number;
  public sourceIndex!: number;
  public dragIndex!: number;

  constructor(
    private notificationService: NotificationService,
    private ologService: OLogService,
    private scfService: ScfV2Service,
    private viewportRuler: ViewportRuler,
    private environmentService: EnvironmentService,
    private testerToolsService: TesterToolsService,
    private bratoiService: BratoiService,
    protected override injector: Injector
  ) {
    super(injector);
  }

  ngOnChanges(_changes: SimpleChanges): void {
    setTimeout(() => {
      this.loadLocations();
      this.loadBratoi();
      this.loadTocas();
      this.getValues();
      this.setDisabled();
      this.loadRequesters();
    }, 100);
  }

  override ngOnDestroy(): void {
    this.resourcesSubs?.unsubscribe();
    this.bratoisSubs?.unsubscribe();
    this.tocasSubs?.unsubscribe();
    this.resourceRelationsSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  initializeForm() {
    this.formGroup = this.formBuilder.group({
      locationsCtrl: [
        { value: '' },
        [Validators.required, utils.IsWhiteSpaceReactiveForm],
      ],
      shieldingsCtrl: [{ value: null }],
      checkAccRFCtrl: [{ value: false }],
      checkBlShCtrl: [{ value: false }],
      checkTocaMovCtrl: [{ value: false }],
      checkPermBlCtrl: [{ value: false }],
      checkPermAccCtrl: [{ value: false }],
      inputWorkHiddenCtrl: [{ value: null }],
      documentCtrl: [{ value: null }],
      relatedLinksCtrl: [{ value: null }],
      requesterCtrl: [
        { value: null },
        [Validators.required, utils.IsWhiteSpaceReactiveForm],
      ],
      estimatedInitCtrl: [{ value: null }],
      estimatedEndCtrl: [{ value: null }],
      notiCheckHPCtrl: [{ value: null }],
      notiCheckBLCtrl: [{ value: null }],
      notiCheckBRCCtrl: [{ value: null }],
      notiCheckRFCtrl: [{ value: null }],
      notiCheckSACtrl: [{ value: null }],
      notiCheckARCtrl: [{ value: null }],
      notiCheckRPCtrl: [{ value: null }],
      inputNotificationsHiddenCtrl: [{ value: null }],
      descriptionCtrl: this.descriptionCtrl,
    });
    if (this.relatedLinksInput) {
      this.relatedLinksInput.nativeElement.value = '';
    }
    this.formGroup.disable();
    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      // Defer to a microtask so formValid()'s side effects (descriptionHasError,
      // control errors) don't mutate bound state during the change-detection pass
      // that's rendering this component — avoids NG0100.
      Promise.resolve().then(() => this.formValid());
    });
  }

  getValues() {
    if (this.scfMaster) {
      if (this.status?.isViewingAmendment) {
        this.scfAmendment = this.scfMaster.scfAmendments?.find((x) => x.active);
        if (this.scfAmendment) {
          this.locations = this.getLocationsFromAmendment(this.scfAmendment);
          this.shieldings = this.getShieldings(
            this.scfAmendment.shieldingAffected
          );
          this.relatedLinks = this.getRelatedLinks(
            this.scfAmendment.relatedLinks
          );
          this.checkAccRFCtrl?.setValue(this.scfAmendment.accRFShielding);
          this.checkBlShCtrl?.setValue(this.scfAmendment.blShielding);
          this.checkTocaMovCtrl?.setValue(this.scfAmendment.tocaMovement);
          this.checkPermBlCtrl?.setValue(this.scfAmendment.permanentBLRemoval);
          this.checkPermAccCtrl?.setValue(
            this.scfAmendment.permanentAccRemoval
          );

          this.setRequester(this.scfAmendment.requesterId);

          this.estimatedInitCtrl?.setValue(this.scfAmendment.estimatedStartOn);
          this.estimatedEndCtrl?.setValue(this.scfAmendment.estimatedEndOn);
          this.documents = this.scfAmendment?.relatedDocuments;
          this.descriptionCtrlText = this.scfAmendment?.description;
          this.notiCheckHPCtrl?.setValue(this.scfAmendment.notifyHP);
          this.notiCheckBLCtrl?.setValue(this.scfAmendment.notifyBLSci);
          this.notiCheckBRCCtrl?.setValue(this.scfAmendment.notifyBRC);
          this.notiCheckRFCtrl?.setValue(this.scfAmendment.notifyRF);
          this.notiCheckSACtrl?.setValue(this.scfAmendment.notifySnA);
          this.notiCheckARCtrl?.setValue(this.scfAmendment.notifyARC);
          this.notiCheckRPCtrl?.setValue(this.scfAmendment.notifyRP);
        }
      } else {
        this.locations = this.scfMaster?.locations;
        this.shieldings = this.scfMaster.shieldingAffected
          ? this.getShieldings(this.scfMaster?.shieldingAffected)
          : [];
        this.documents = this.scfMaster?.relatedDocuments;
        this.relatedLinks = this.scfMaster.relatedLinks
          ? this.getRelatedLinks(this.scfMaster?.relatedLinks)
          : [];
        this.checkBlShCtrl?.setValue(this.scfMaster?.blShielding);
        this.checkTocaMovCtrl?.setValue(this.scfMaster?.tocaMovement);
        this.checkPermBlCtrl?.setValue(this.scfMaster?.permanentBLRemoval);
        this.checkPermAccCtrl?.setValue(this.scfMaster?.permanentAccRemoval);

        this.setRequester(this.scfMaster.requesterId);

        this.estimatedInitCtrl?.setValue(this.scfMaster?.estimatedStartOn);
        this.estimatedEndCtrl?.setValue(this.scfMaster?.estimatedEndOn);
        this.descriptionCtrlText = this.scfMaster?.description
          ? this.scfMaster?.description
          : null;
        this.notiCheckHPCtrl?.setValue(this.scfMaster.notifyHP);
        this.notiCheckBLCtrl?.setValue(this.scfMaster.notifyBLSci);
        this.notiCheckBRCCtrl?.setValue(this.scfMaster.notifyBRC);
        this.notiCheckRFCtrl?.setValue(this.scfMaster.notifyRF);
        this.notiCheckSACtrl?.setValue(this.scfMaster.notifySnA);
        this.notiCheckARCtrl?.setValue(this.scfMaster.notifyARC);
        this.notiCheckRPCtrl?.setValue(this.scfMaster.notifyRP);
        if (this.checkAccRFCtrl) {
          this.checkAccRFCtrl?.setValue(this.scfMaster?.accRFShielding);
        }
      }
      this.notiCheckHPCtrl?.disable();
      if (
        this.locations?.some((l) => l.type === ResourceType.Beamline) ??
        false
      )
        this.notiCheckBLCtrl?.disable();
      if (this.checkPermBlCtrl?.value) this.notiCheckBRCCtrl?.disable();
      if (this.checkPermAccCtrl?.value) this.notiCheckARCtrl?.disable();
      if (this.checkPermAccCtrl?.value || this.checkPermBlCtrl?.value)
        this.notiCheckRPCtrl?.disable();
      if (this.checkTocaMovCtrl?.value) this.notiCheckSACtrl?.disable();
      this.setNotificationChecks(WorkRequired.None, false);
      this.loadLocations();
    } else {
      this.locations = null;
      this.shieldings = [];
      this.documents = null;
      this.relatedLinks = [];

      this.checkBlShCtrl?.setValue(false);
      this.checkTocaMovCtrl?.setValue(false);
      this.checkPermBlCtrl?.setValue(false);
      this.checkPermAccCtrl?.setValue(false);
      this.requesterCtrl?.setValue(null);
      this.estimatedInitCtrl?.setValue(null);
      this.estimatedEndCtrl?.setValue(null);
      this.descriptionCtrlText = null;
      this.notiCheckHPCtrl?.setValue(false);
      this.notiCheckBLCtrl?.setValue(false);
      this.notiCheckBRCCtrl?.setValue(false);
      this.notiCheckRFCtrl?.setValue(false);
      this.notiCheckSACtrl?.setValue(false);
      this.notiCheckARCtrl?.setValue(false);
      this.notiCheckRPCtrl?.setValue(false);
    }
    this.updateRemoveBlocked();
    //this.loading.emit(false);
  }

  /**
   * Sets the requester based on the provided ID. If the ID corresponds to a user
   * in the `users` list and the user is not already in the `requesters` list,
   * the user is added to the `requesters` list. Additionally, updates the
   * `requesterCtrl` form control with the requester's ID.
   *
   * @param id - The ID of the requester to set. If not provided, the method does nothing.
   */
  setRequester(id?: number) {
    if (id) {
      const requester = this.users.find((u) => u.id == id);
      if (!this.requesters) {
        this.requesters = [];
      }
      if (requester) {
        if (!this.requesters?.map((r) => r.id).includes(requester.id)) {
          this.requesters.push({
            id: requester.id,
            name: requester.name,
          } as User);
        }
        this.requesterCtrl?.setValue(requester);
      }
    }
    else this.requesterCtrl?.setValue(null);
  }

  /**
   * Compares two User objects to determine if they are the same based on their `id` property.
   *
   * @param a - The first User object to compare.
   * @param b - The second User object to compare.
   * @returns `true` if both User objects have the same `id`, or if both are `null` or `undefined`; otherwise, `false`.
   */
  compareRequesters = (a: User, b: User) => a?.id === b?.id;

  /**
   * Checks if a given user exists in the list of users.
   *
   * @param user - The user object to check for in the list.
   * @returns `true` if the user is found in the list, otherwise `false`.
   */
  isUserInList(user: User): boolean {
    return this.requesters?.some((u) => u.id === user?.id);
  }

  /**
   * Parses a JSON string to extract an array of related links.
   *
   * @param relatedLinksString - A JSON string representing an array of related links.
   * Each link should have properties `URL` and `Text` to be considered valid.
   *
   * @returns An array of `RelatedLinkBase` objects. If the input string is empty or invalid,
   * an empty array is returned. If the parsed array contains objects with `URL` and `Text`,
   * they are mapped to `RelatedLinkBase` objects. Otherwise, the parsed array is returned as-is.
   */
  getRelatedLinks(relatedLinksString: string): RelatedLinkBase[] {
    if (relatedLinksString) {
      const relatedLinksArray = utils.JSONparse(relatedLinksString);
      let relatedLinks: RelatedLinkBase[] = [];
      if (relatedLinksArray.some((r: any) => r.URL && r.Text)) {
        // Use RelatedLink
        relatedLinksArray.map((r: RelatedLink) => {
          relatedLinks.push({
            text: r.Text,
            url: r.URL,
            formID: 1,
            id: 0,
          } as RelatedLinkBase);
        });
      } else {
        relatedLinks = relatedLinksArray;
      }
      return relatedLinks;
    }
    return [];
  }

  getLocationsFromAmendment(scfAmendment: SCFAmendment) {
    if (!this.resources?.length) {
      this.loadLocations();
    }
    const locations: Location[] = [];
    const locationIds: string[] = scfAmendment.locationIds?.split(',');
    const locationNames: string[] = scfAmendment.locationNames?.split(',');
    locationIds.map((id, i) => {
      const location = {
        id: +id,
        name: locationNames[i],
        type: this.resources?.find((x) => x.id == +id)?.type,
      } as Location;
      locations.push(location);
    });
    return locations;
  }

  setValues() {
    if (this.scfMaster) {
      if (!this.status?.isViewingAmendment) {
        this.scfMaster.description = this.descriptionCtrlText ?? '';
        this.scfMaster.locations = this.locations ?? [];
        this.scfMaster.locationIds = this.locations?.map((l) => l.id).join(',');
        this.scfMaster.locationNames = this.locations
          ?.map((l) => l.name)
          .join(', ');
        this.scfMaster.shieldingAffected = this.normalizeShieldings(this.shieldings);
        this.scfMaster.relatedDocuments = this.documents ?? [];
        this.scfMaster.relatedLinks = utils.JSONstringify(this.relatedLinks);
        this.scfMaster.accRFShielding = this.checkAccRFCtrl?.value;
        this.scfMaster.blShielding = this.checkBlShCtrl?.value;
        this.scfMaster.tocaMovement = this.checkTocaMovCtrl?.value;
        this.scfMaster.permanentBLRemoval = this.checkPermBlCtrl?.value;
        this.scfMaster.permanentAccRemoval = this.checkPermAccCtrl?.value;
        this.scfMaster.requesterId = this.requesterCtrl?.value?.id;
        this.scfMaster.estimatedStartOn = this.estimatedInitCtrl?.value;
        this.scfMaster.estimatedEndOn = this.estimatedEndCtrl?.value;
        this.scfMaster.notifyARC = this.notiCheckARCtrl?.value;
        this.scfMaster.notifyRP = this.notiCheckRPCtrl?.value;
        this.scfMaster.notifyBLSci = this.notiCheckBLCtrl?.value;
        this.scfMaster.notifyBRC = this.notiCheckBRCCtrl?.value;
        this.scfMaster.notifyHP = this.notiCheckHPCtrl?.value;
        this.scfMaster.notifyRF = this.notiCheckRFCtrl?.value;
        this.scfMaster.notifySnA = this.notiCheckSACtrl?.value;
        if (
          this.locations?.some((l) => l.type === ResourceType.Beamline) ??
          false
        )
          this.notiCheckBLCtrl?.disable();
        else this.notiCheckBLCtrl?.enable();
      } else {
        if (this.scfAmendment) {
          this.scfAmendment.description = this.descriptionCtrlText ?? '';
          this.scfAmendment.locations = this.locations ?? [];
          this.scfAmendment.locationIds =
            this.locations?.map((l) => l.id).join(',') ?? '';
          this.scfAmendment.locationNames =
            this.locations?.map((l) => l.name).join(',') ?? '';
          this.scfAmendment.shieldingAffected = this.normalizeShieldings(this.shieldings);
          this.scfAmendment.relatedDocuments = this.documents ?? [];
          this.scfAmendment.relatedLinks = utils.JSONstringify(
            this.relatedLinks
          );
          this.scfAmendment.accRFShielding = this.checkAccRFCtrl?.value;
          this.scfAmendment.blShielding = this.checkBlShCtrl?.value;
          this.scfAmendment.tocaMovement = this.checkTocaMovCtrl?.value;
          this.scfAmendment.permanentBLRemoval = this.checkPermBlCtrl?.value;
          this.scfAmendment.permanentAccRemoval = this.checkPermAccCtrl?.value;
          this.scfAmendment.requesterId = this.requesterCtrl?.value?.id;
          this.scfAmendment.estimatedStartOn = this.estimatedInitCtrl?.value;
          this.scfAmendment.estimatedEndOn = this.estimatedEndCtrl?.value;
          this.scfAmendment.notifyARC = this.notiCheckARCtrl?.value;
          this.scfAmendment.notifyRP = this.notiCheckRPCtrl?.value;
          this.scfAmendment.notifyBLSci = this.notiCheckBLCtrl?.value;
          this.scfAmendment.notifyBRC = this.notiCheckBRCCtrl?.value;
          this.scfAmendment.notifyHP = this.notiCheckHPCtrl?.value;
          this.scfAmendment.notifyRF = this.notiCheckRFCtrl?.value;
          this.scfAmendment.notifySnA = this.notiCheckSACtrl?.value;
          if (
            this.locations?.some((l) => l.type === ResourceType.Beamline) ??
            false
          )
            this.notiCheckBLCtrl?.disable();
          else this.notiCheckBLCtrl?.enable();
        }
      }
      this.setFormDirty();
      this.formValid();
      this.updateRemoveBlocked();
      this.changed.emit(this.scfMaster);
    }
  }

  /**
   * Whether the tester-only "auto-populate" tooling should be shown.
   * Gated defensively: only on non-production builds AND for System
   * Administrators, so it can never surface for real users in production.
   */
  get showTestTools(): boolean {
    return !this.environmentService.isProduction() && this.isSA() && this.testerToolsService.enabled;
  }

  /**
   * Tester convenience: fills the Section 1 required fields with valid dummy data
   * so a form can be brought to a submittable Draft in one click, mirroring the
   * same code paths a real user action would take. Persisting is left to the
   * normal Save/Submit buttons. See showTestTools for the access gate.
   *
   * Three presets:
   * - 'happy'  : an accelerator location + a beamline location, one piece of
   *              shielding mentioned from each, "Remove & Replace" work type.
   * - 'complex': accelerator location + BL 4.0.2 (known shared shielding), a
   *              shared shielding + a TOCA mentioned, a "Permanently Remove /
   *              Modify" work type, an external link, an ASCC link, and a custom
   *              restriction.
   * - 'random' : a random location + a random piece of its shielding (if any), a
   *              random start date (+7 days completion), any user with the
   *              Requester privilege, and a random one of the three work types.
   *              Not truly random — just varied, for broader path coverage.
   *
   * Shielding is entered through the description "@"-mention feature (not the
   * deprecated shielding field): the mentionsChanged -> selectedShl path also
   * auto-populates the restriction field, and shared shielding pulls in its other
   * parent location/restriction automatically.
   */
  autoPopulate(path: 'happy' | 'complex' | 'random'): void {
    if (!this.scfMaster || !this.showTestTools) return;

    // Make sure resource/relation data is available for shielding resolution.
    this.loadLocations();

    // Note: fields are intentionally NOT cleared first — re-running or switching
    // presets is additive (overlap is allowed for now).

    // 1. Locations + shielding-by-mention (preset-specific). Each returns the
    //    shielding/TOCA it entered so we can describe it below.
    let enteredShieldings: ResourceRelation[];
    if (path === 'complex') {
      enteredShieldings = this.selectComplexLocationsAndShielding();
    } else if (path === 'random') {
      enteredShieldings = this.selectRandomLocationAndShielding();
    } else {
      enteredShieldings = this.selectHappyLocationsAndShielding();
    }

    // 2. Requester — Happy uses a user holding the RWPADes role; Complex uses a
    //    valid requester (has the Requester privilege) who does NOT hold it;
    //    Random uses any user with the Requester privilege.
    if (path === 'complex') {
      this.setRequesterMatching(
        (u) =>
          this.hasPrivilegeByUser(PrivilegeEnum.Requester, u.id!) &&
          !this.userHasRole(u, Roles.RWPADes)
      );
    } else if (path === 'random') {
      this.setRandomRequester();
    } else {
      this.setRequesterMatching((u) => this.userHasRole(u, Roles.RWPADes));
    }

    // 3. Estimated dates and the description. These (and the links below) are set
    //    BEFORE the work-type checkboxes because checking a "Permanently
    //    Remove/Modify" box triggers section 5 saveSCFMaster() — anything not yet
    //    written to the model when that save fires would be lost on the refresh
    //    (that was the dates reverting to 1/1/1).
    if (path === 'random') {
      this.fillRandomEstimatedDates();
    } else {
      this.fillEstimatedDates();
    }
    // Append (don't overwrite) a numbered line describing this run, so repeated
    // auto-fills keep their history, e.g. "1. Happy Path. ...", "2. Random Path. ...".
    this.appendDescriptionEntry(
      this.pathLabel(path),
      enteredShieldings.length
        ? `Affected shielding entered via @-mention: ${this.buildShieldingMentionSpans(enteredShieldings)}.`
        : 'No shielding selected for this entry.'
    );

    // 4. Links and a custom restriction. Persist immediately so they're part of
    //    the model before any work-type save. Complex adds an external link, a
    //    (first-valid) ASCC link, and a custom restriction; Random adds a random
    //    ASCC link.
    if (path === 'complex') {
      this.addExternalLink('ALS Beam Status', 'https://als.lbl.gov/beam-status/');
      this.addAsccLink(false);
      this.setValues();
      // Routed up to Section 2 (which owns restrictions) via the parent.
      this.customRestrictionRequested.emit(
        'Auto-populated custom restriction for testing.'
      );
    } else if (path === 'random') {
      this.addAsccLink(true);
      this.setValues();
    }

    // Persist what we have so far, then validate.
    this.setValues();
    this.formValid();

    // 5. Type of work — set explicitly per preset (no "any already checked"
    //    guard): adding a TOCA above silently checks the hidden TocaMovement box,
    //    which would otherwise suppress the visible "Add ..." checkboxes. Complex
    //    sets a single "Add, Permanently Remove, Modify, or Align ... Shielding"
    //    option (one save; also avoids the double-save that duplicated the ASCC
    //    link).
    //
    //    Deferred to a macrotask so it runs AFTER the location restrictions, which
    //    are added by selectedLoc's own deferred (setTimeout) locationAdded emits.
    //    Checking a Permanent option triggers section 5 saveSCFMaster(); if that
    //    fired before the auto-generated restrictions were in the model, the save
    //    (and its refresh) would drop them, leaving only the custom restriction.
    setTimeout(() => {
      if (path === 'complex') {
        this.setWorkType(this.checkPermBlCtrl, WorkRequired.PermanentBLRemoval);
      } else if (path === 'random') {
        const choice = this.pickRandom(this.workTypeChoices());
        if (choice) this.setWorkType(choice.ctrl, choice.work);
      } else {
        this.setWorkType(this.checkAccRFCtrl, WorkRequired.AccRFShielding);
      }
      this.setValues();
      this.formValid();
    });
  }

  /**
   * Asks the parent to run the Reset routine. The parent orchestrates it across
   * sections (clear fields → clear restrictions → save the cleared master →
   * clear Operational Approval checks) because clearing op-approvals persists via
   * SaveTasks, whose SignalR refresh reloads the master — so the cleared state
   * must be SAVED first or the refresh repopulates the fields.
   */
  requestReset(): void {
    if (!this.scfMaster || !this.showTestTools) return;
    this.resetRequested.emit();
  }

  /**
   * Clears the Section 1 fields the presets populate (locations, shielding, work
   * types, requester, links, ASCC documents, notifications) and resets the
   * estimated dates to their defaults (today / today + 7) rather than blanking
   * them (an empty date control renders as 1/1/1). Writes the cleared values into
   * the (shared) scfMaster via setValues so the parent's subsequent save persists
   * them. Restrictions (Section 2) and Operational Approval checks (Section 5) are
   * cleared by the parent.
   */
  clearFieldsForReset(): void {
    this.locations = [];
    this.shieldings = [];
    this.locationsCtrl?.setValue([]);
    this.shieldingsCtrl?.setValue(null);

    this.checkAccRFCtrl?.setValue(false);
    this.checkBlShCtrl?.setValue(false);
    this.checkPermBlCtrl?.setValue(false);
    this.checkPermAccCtrl?.setValue(false);
    this.checkTocaMovCtrl?.setValue(false);

    this.descriptionCtrlText = null;
    this.descriptionCtrl.setValue(null);
    this.autoFillEntryCount = 0;

    this.requesterCtrl?.setValue(null);

    // Reset dates to defaults rather than null (a blank date renders as 1/1/1).
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 7);
    this.estimatedInitCtrl?.setValue(start);
    this.estimatedEndCtrl?.setValue(end);

    this.relatedLinks = [];
    this.documents = [];

    if (this.scfMaster) {
      this.scfMaster.notifyHP = false;
      this.scfMaster.notifyBLSci = false;
      this.scfMaster.notifyBRC = false;
      this.scfMaster.notifyRF = false;
      this.scfMaster.notifySnA = false;
      this.scfMaster.notifyARC = false;
      this.scfMaster.notifyRP = false;
    }
    this.notiCheckHPCtrl?.setValue(false);
    this.notiCheckBLCtrl?.setValue(false);
    this.notiCheckBRCCtrl?.setValue(false);
    this.notiCheckRFCtrl?.setValue(false);
    this.notiCheckSACtrl?.setValue(false);
    this.notiCheckARCtrl?.setValue(false);
    this.notiCheckRPCtrl?.setValue(false);

    this.setValues();
    this.formValid();
  }

  /**
   * Random path: a random location, plus a random named piece of its shielding
   * (if any) entered via "@"-mention. "Random" here means varied, not seeded —
   * fine for a non-production tester aid.
   */
  private selectRandomLocationAndShielding(): ResourceRelation[] {
    const location = this.pickRandom(this.allLocations ?? []);
    if (!location) {
      this.selectFallbackLocation();
      return [];
    }
    this.selectLocations([location]);
    this.loadRelatedShieldings();
    // Any named entry the location offers — shielding (positive id) OR a TOCA
    // (negative id). Excludes manual/blank entries (id 0).
    const shieldings = (this.allShieldings ?? []).filter(
      (s) => !!s.childResourceID && !!s.childResourceName
    );
    const shielding = this.pickRandom(shieldings);
    return shielding ? this.enterShieldingByMention([shielding]) : [];
  }

  /** Sets the requester to a random user holding the Requester privilege. */
  private setRandomRequester(): void {
    const candidates = this.getUsers().filter(
      (u) => !!u.id && this.hasPrivilegeByUser(PrivilegeEnum.Requester, u.id!)
    );
    const pick = this.pickRandom(candidates);
    if (pick?.id) {
      this.setRequester(pick.id);
      return;
    }
    this.loadRequesters();
    if (this.requesters?.length) this.setRequester(this.requesters[0].id);
  }

  /** A random valid start date (today..+29 days) with completion 7 days later. */
  private fillRandomEstimatedDates(): void {
    const start = new Date();
    start.setDate(start.getDate() + Math.floor(Math.random() * 30));
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    this.estimatedInitCtrl?.setValue(start);
    this.estimatedEndCtrl?.setValue(end);
  }

  /** The three selectable work-type options the Random preset picks from. */
  private workTypeChoices(): { ctrl: AbstractControl | null; work: WorkRequired }[] {
    return [
      { ctrl: this.checkAccRFCtrl, work: WorkRequired.AccRFShielding },
      { ctrl: this.checkPermBlCtrl, work: WorkRequired.PermanentBLRemoval },
      { ctrl: this.checkPermAccCtrl, work: WorkRequired.PermanentAccRemoval },
    ];
  }

  /** Returns a random element of `items`, or undefined when empty. */
  private pickRandom<T>(items: T[]): T | undefined {
    if (!items?.length) return undefined;
    return items[Math.floor(Math.random() * items.length)];
  }

  /** Happy path: one accelerator + one beamline location, shielding from each. */
  private selectHappyLocationsAndShielding(): ResourceRelation[] {
    const accelerator = this.probeLocationForShielding((l) =>
      this.isAcceleratorLocation(l)
    );
    const beamline = this.probeLocationForShielding((l) =>
      this.isBeamlineLocation(l)
    );
    const chosen = [accelerator, beamline].filter(
      (x): x is { location: Location; shielding: ResourceRelation } => !!x
    );
    if (!chosen.length) {
      this.selectFallbackLocation();
      return [];
    }
    this.selectLocations(chosen.map((c) => c.location));
    this.loadRelatedShieldings();
    return this.enterShieldingByMention(chosen.map((c) => c.shielding));
  }

  /**
   * Complex path: an accelerator location plus BL 4.0.2 (a known case of shared
   * shielding). Mentions one shared shielding (which auto-adds its other parent
   * location/restriction) and one TOCA from the BL 4.0.2 list, alongside the
   * accelerator shielding.
   */
  private selectComplexLocationsAndShielding(): ResourceRelation[] {
    const accelerator = this.probeLocationForShielding((l) =>
      this.isAcceleratorLocation(l)
    );
    const bl402 = this.findLocationByName('4.0.2', (l) =>
      this.isBeamlineLocation(l)
    );

    const locations = [accelerator?.location, bl402].filter(
      (l): l is Location => !!l
    );
    if (!locations.length) {
      this.selectFallbackLocation();
      return [];
    }

    // Resolve each piece of shielding from the location that owns it, so the
    // shared shielding genuinely comes from BL 4.0.2 (not another accelerator
    // shielding that also happens to be shared). One Booster Ring shielding, one
    // BL 4.0.2 shared shielding, one BL 4.0.2 TOCA — deduped.
    const bl402Shieldings = bl402 ? this.shieldingsForLocation(bl402) : [];
    const sharedShielding =
      bl402Shieldings.find(
        (s) =>
          (s.childResourceID ?? 0) > 0 &&
          s.childResourceType !== ResourceType.TOCA &&
          !!s.childResourceName &&
          s.shared
      ) ??
      bl402Shieldings.find(
        (s) =>
          (s.childResourceID ?? 0) > 0 &&
          s.childResourceType !== ResourceType.TOCA &&
          !!s.childResourceName
      );
    const toca = bl402Shieldings.find(
      (s) => s.childResourceType === ResourceType.TOCA && !!s.childResourceName
    );

    this.selectLocations(locations);
    this.loadRelatedShieldings();

    const shieldings = this.dedupeShieldings([
      accelerator?.shielding,
      sharedShielding,
      toca,
    ]);
    return this.enterShieldingByMention(shieldings);
  }

  /**
   * Returns the shielding a single location offers, by temporarily selecting it
   * and running the form's own loadRelatedShieldings(). Restores the current
   * selection before returning. Used to attribute shielding to the right location.
   */
  private shieldingsForLocation(location: Location): ResourceRelation[] {
    const savedLocations = this.locations;
    this.locations = [location];
    this.loadRelatedShieldings();
    const result = [...(this.allShieldings ?? [])];
    this.locations = savedLocations;
    return result;
  }

  /** Drops null/undefined entries and de-duplicates shielding by childResourceID. */
  private dedupeShieldings(
    shieldings: (ResourceRelation | null | undefined)[]
  ): ResourceRelation[] {
    const result: ResourceRelation[] = [];
    const seen = new Set<number>();
    for (const s of shieldings) {
      const id = s?.childResourceID;
      if (s && id != null && !seen.has(id)) {
        seen.add(id);
        result.push(s);
      }
    }
    return result;
  }

  /** Selects each location through the real selectedLoc() path. */
  private selectLocations(locations: Location[]): void {
    // selectedLoc defers its locationAdded emit via setTimeout, so each
    // location's restrictions are seeded in its own change-detection cycle —
    // otherwise a synchronous second emit overwrites the first before
    // restriction-available-autocomplete can process it.
    locations.forEach((location) => {
      const event = {
        option: { value: location },
      } as MatAutocompleteSelectedEvent;
      this.selectedLoc(event);
    });
  }

  /**
   * Enters the given shielding/TOCAs through the "@"-mention feature: emits the
   * selected mention ids, driving the same mentionsChanged -> selectedShl path a
   * user triggers (which adds the shielding and auto-populates restrictions).
   * Returns the entries actually mentioned so the caller can describe them.
   */
  private enterShieldingByMention(
    shieldings: ResourceRelation[]
  ): ResourceRelation[] {
    const valid = shieldings.filter(
      (s): s is ResourceRelation => !!s && s.childResourceID != null
    );
    if (!valid.length) return [];
    // mentionsChanged removes any shielding whose id isn't in the array it
    // receives, so pass the union of already-selected ids and the new ones —
    // otherwise a second auto-fill run would strip the first run's shielding.
    const existingIds = (this.shieldings ?? []).map((s) => s.childResourceID);
    const ids = Array.from(
      new Set([...existingIds, ...valid.map((s) => s.childResourceID)])
    );
    this.mentionsChanged(ids);
    return valid;
  }

  /** When no shielding-bearing location is found, still satisfy the location field. */
  private selectFallbackLocation(): void {
    const firstLocation = this.allLocations?.[0];
    if (firstLocation && !this.locations?.length) {
      const event = {
        option: { value: firstLocation },
      } as MatAutocompleteSelectedEvent;
      this.selectedLoc(event);
    }
  }

  private isAcceleratorLocation(l: Location): boolean {
    return (
      l.type === ResourceType.Accelerator ||
      l.type === ResourceType.AcceleratorLocation
    );
  }

  private isBeamlineLocation(l: Location): boolean {
    return l.type === ResourceType.Beamline || l.type === ResourceType.Branchline;
  }

  /** Sets a work-type checkbox and runs the same handler the template (change) uses. */
  private setWorkType(
    ctrl: AbstractControl | null,
    workRequired: WorkRequired
  ): void {
    ctrl?.setValue(true);
    this.setNotificationChecks(workRequired, { checked: true });
  }

  /** Whether a user holds the given role. */
  private userHasRole(user: User, roleId: Roles): boolean {
    return user.userRole?.some((ur) => ur.roleID === roleId) ?? false;
  }

  /**
   * Sets the requester to the first user matching `predicate`. Overrides any
   * existing value so each preset gets its intended requester. Falls back to any
   * requester so the required field stays satisfied.
   */
  private setRequesterMatching(predicate: (user: User) => boolean): void {
    const match = this.getUsers().find((u) => !!u.id && predicate(u));
    if (match?.id) {
      this.setRequester(match.id);
      return;
    }
    this.loadRequesters();
    if (this.requesters?.length) this.setRequester(this.requesters[0].id);
  }

  /** Adds an external link to the Related Documents and Links field. */
  private addExternalLink(text: string, url: string): void {
    if (this.relatedLinks?.some((l) => l.url === url)) return;
    this.relatedLinks.push({ text, url, formID: 1, id: 0 } as RelatedLinkBase);
    this.relatedLinksCtrl?.markAsDirty();
  }

  /**
   * Adds an ASCC link to a real form/checklist from the document store, building
   * the RelatedDocument the same way the related-documents control's selectedDoc()
   * does (filtering out templates, drafts and canceled documents). `pickRandomly`
   * chooses a random candidate (Random preset) instead of the first valid one.
   */
  private addAsccLink(pickRandomly: boolean): void {
    const docs =
      (this.store.selectSnapshot((state: any) => state.Documents?.data) as
        | Document[]
        | undefined) ?? [];
    const candidates = docs.filter(
      (d) =>
        d.category !== DocumentCategory.ChecklistTemplate &&
        (d.status ?? 0) > FormStatusEnum.New &&
        (d.status ?? 0) !== FormStatusEnum.Canceled &&
        !!d.serialNo &&
        !(d.formID === this.scfMaster?.id && d.type === FormType.SCF)
    );
    if (!candidates.length) return;
    const pick = pickRandomly ? this.pickRandom(candidates)! : candidates[0];
    const exists = this.documents?.some(
      (d) =>
        d.relatedDocumentId === pick.formID &&
        d.relatedDocumentTypeId === (pick.type ?? 0)
    );
    if (exists) return;
    const related: RelatedDocument = {
      originalDocumentId: this.scfMaster?.id,
      originalDocumentTypeId: FormType.SCF,
      relatedDocumentId: pick.formID,
      relatedDocumentStatus: pick.status,
      relatedDocumentSerialNo: pick.serialNo,
      relatedDocumentTypeId: pick.type ?? 0,
    };
    this.documents = [...(this.documents ?? []), related];
  }

  /** Fills estimated start/end with valid values (end >= start >= today). */
  private fillEstimatedDates(): void {
    if (!this.estimatedInitCtrl?.value) {
      this.estimatedInitCtrl?.setValue(new Date());
    }
    if (!this.estimatedEndCtrl?.value) {
      const end = new Date();
      end.setDate(end.getDate() + 7);
      this.estimatedEndCtrl?.setValue(end);
    }
  }

  /**
   * Finds the first location whose (whitespace-insensitive) name contains
   * `needle` and matches `matchType`, e.g. the BL 4.0.2 beamline.
   */
  private findLocationByName(
    needle: string,
    matchType: (location: Location) => boolean
  ): Location | null {
    const target = needle.replace(/\s/g, '').toLowerCase();
    return (
      (this.allLocations ?? []).find(
        (l) =>
          matchType(l) &&
          (l.name ?? '').replace(/\s/g, '').toLowerCase().includes(target)
      ) ?? null
    );
  }

  /**
   * Finds the first location matching `matchType` that actually offers shielding,
   * using the form's own loadRelatedShieldings(). Returns the location and a real
   * shielding relation (one with a name, so it never renders as "undefined"),
   * skipping TOCAs. Picking from allShieldings (rather than the mention list)
   * keeps accelerator shielding working, since AcceleratorLocation shielding is
   * excluded from the "@" dropdown groups but is still valid shielding. Restores
   * the current location selection before returning.
   */
  private probeLocationForShielding(
    matchType: (location: Location) => boolean
  ): { location: Location; shielding: ResourceRelation } | null {
    const savedLocations = this.locations;
    let result: { location: Location; shielding: ResourceRelation } | null = null;
    for (const candidate of this.allLocations ?? []) {
      if (!matchType(candidate)) continue;
      this.locations = [candidate];
      this.loadRelatedShieldings();
      const shielding = this.allShieldings?.find(
        (s) =>
          (s.childResourceID ?? 0) > 0 &&
          s.childResourceType !== ResourceType.TOCA &&
          !!s.childResourceName
      );
      if (shielding) {
        result = { location: candidate, shielding };
        break;
      }
    }
    this.locations = savedLocations;
    return result;
  }

  /**
   * Builds the comma-separated "@"-mention spans for the given shielding/TOCAs,
   * matching the markup the comments-box editor produces (StyledMentionBlot): a
   * `span.mention` whose text is the name, carrying data-id/data-value plus
   * data-title (KE Text) and data-windchill-links so the KE Text / links menu
   * works when the mention is clicked. The KE text / links come from the same
   * resource lookup getMentions() uses. (TOCAs have a negative id and no resource,
   * so they simply render without a KE Text menu.)
   */
  private buildShieldingMentionSpans(shieldings: ResourceRelation[]): string {
    return shieldings
      .map((s) => {
        const id = s.childResourceID;
        const name = this.escapeAttr(s.childResourceName ?? `Shielding ${id}`);
        const resource = this.resources?.find((x) => x.id === id);
        const hasTitle = !!resource?.keText;
        const hasLinks = !!resource?.windchillLinks?.length;
        const title = hasTitle
          ? ` data-title="${this.escapeAttr(resource!.keText!)}"`
          : '';
        const links = hasLinks
          ? ` data-windchill-links="${this.escapeAttr(
            JSON.stringify(resource!.windchillLinks)
          )}"`
          : '';
        // Colour the mention the same way the editor's dropdown does (red when
        // shared, darkorange for TOCAs, otherwise #333). The blot reads it from
        // data-color on re-parse; the inline style colours the read-only view.
        const color = this.mentionColor(s);
        // Match what the Quill StyledMentionBlot renders so the mention survives
        // re-parsing in the editor: an empty data-denotation-char (otherwise the
        // editor renders the literal text "undefined" before the name) and the
        // blot's presentation (medium-weight class, colour, and clickable
        // underline when it has KE text or links).
        const style =
          `color: ${color};` +
          (hasTitle || hasLinks
            ? ' cursor: pointer; text-decoration: underline;'
            : '');
        return (
          `<span class="mention mention-chip-medium" data-denotation-char="" data-id="${id}" ` +
          `data-value="${name}" data-color="${color}"${title}${links} ` +
          `style="${style}">${name}</span>`
        );
      })
      .join(', ');
  }

  /** Human-readable label for a preset, used in the description history. */
  private pathLabel(path: 'happy' | 'complex' | 'random'): string {
    return path === 'complex'
      ? 'Complex Path'
      : path === 'random'
        ? 'Random Path'
        : 'Happy Path';
  }

  /**
   * Appends a numbered line to the description instead of overwriting it, so
   * repeated auto-fills keep their history (e.g. "1. Happy Path. ...",
   * "2. Random Path. ..."). Each entry is its own paragraph so the mention spans
   * survive Quill re-parsing.
   */
  private appendDescriptionEntry(label: string, body: string): void {
    const entry = `${++this.autoFillEntryCount}. ${label}. ${body}`;
    const existing = this.descriptionCtrlText?.trim() ?? '';
    this.descriptionCtrlText = `${existing}<p>${entry}</p>`;
  }

  /**
   * Mirrors the comments-box dropdown's renderItem colour logic so auto-filled
   * mentions match manually-entered ones: red when the shielding is shared,
   * darkorange for TOCAs (childResourceType > 99), otherwise the default #333.
   */
  private mentionColor(s: ResourceRelation): string {
    const isShared =
      s.parentResourceType === ResourceType.Shutter
        ? !!s.shared
        : (s.sharedWith?.length ?? 0) > 0;
    if (isShared) return 'red';
    if ((s.childResourceType ?? 0) > 99) return 'darkorange';
    return '#333';
  }

  /** Escapes a string for safe inclusion in an HTML attribute. */
  private escapeAttr(value: string): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  normalizeShieldings(shieldings?: ResourceRelation[]) {
    return this.utils.JSONstringify(
      shieldings?.map((s) => {
        s.parentResource = null;
        s.childResource = null;
        return s;
      })
    );
  }

  setDisabled() {
    if (this.disabled) {
      this.formGroup?.disable();
      this.disableControls = true;
    } else if (
      this.status &&
      (this.status.isCreating ||
        this.status.isEditing ||
        this.status.isCreatingAmendment ||
        this.status.isEditingAmendment)
    ) {
      this.formGroup?.enable();
      this.disableControls = false;
      this.notiCheckHPCtrl?.disable();
      if (
        this.locations?.some((l) => l.type === ResourceType.Beamline) ??
        false
      )
        this.notiCheckBLCtrl?.disable();
      if (this.checkPermBlCtrl?.value) this.notiCheckBRCCtrl?.disable();
      if (this.checkPermAccCtrl?.value) this.notiCheckARCtrl?.disable();
      if (this.checkPermAccCtrl?.value || this.checkPermBlCtrl?.value)
        this.notiCheckRPCtrl?.disable();
      if (this.checkTocaMovCtrl?.value) this.notiCheckSACtrl?.disable();
    } else {
      this.formGroup?.disable();
      this.disableControls = true;
    }
    this.relatedLinksCtrl?.disable();
  }

  loadRequesters() {
    this.requesters = this.getActiveUsers().filter(u => this.hasPrivilegeByUser(PrivilegeEnum.Requester, u.id!));
  }
  //#region Locations

  loadResourceRelations() {
    this.resourceRelations$ = this.store.select(
      (state) => state.ResourceRelations.data
    );
    this.resourceRelationsSubs = this.resourceRelations$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data.length) {
        this.resourceRelations = data;
        this.resourceRelations.map(
          (r) =>
          (r.parentResource = this.resources.find(
            (x) => x.id == r.parentResourceID
          ))
        );
        // Relations feed checkRemove(); refresh the precomputed map now they're loaded.
        this.updateRemoveBlocked();
      }
    });
  }

  loadLocations() {
    this.resources$ = this.store.select((state) => state.Resources.data);
    this.resourcesSubs = this.resources$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data.length) {
        this.resources = data;
        this.allLocations =
          this.resources
            .filter((x) => x.status !== 0 && x.resourceType?.location && (x.statusResourceId == StatusResource.Active || x.statusResourceId == StatusResource.Commissioning || !x.statusResourceId))
            ?.map((x) => {
              const location: Location = {
                id: x.id,
                name: x.name,
                type: x.type,
                resourceType: x.resourceType,
                code: x.code,
              };
              return location;
            })
            .sort((a, b) =>
              utils.sortArrayAlphabeticallyWithComplexNumbers(
                a.name ?? '',
                b.name ?? ''
              )
            ) ?? [];

        this.filteredLocations = this.allLocations.filter(
          (x) => !this.locations?.map((l) => l.id).includes(x.id)
        );
        this.createLocationGroups();
        this.loadRelatedShieldings();
        this.loadResourceRelations();
      }
    });
  }

  createLocationGroups() {
    this.locationGroups = [];
    this.filteredLocations.map((l) => {
      if (l.type == ResourceType.Accelerator) {
        if (!this.locationGroups.map((g) => g.id).includes(l.id)) {
          const relations = this.resourceRelations?.filter(
            (rr) =>
              rr.parentResourceID == l.id &&
              !this.locations?.map((x) => x.id).includes(rr.childResourceID)
          );
          const locs = relations
            ?.filter(
              (l) => l.childResourceType == ResourceType.AcceleratorLocation
            )
            .map((rr) => {
              return {
                id: rr.childResourceID,
                name: rr.childResourceName,
                type: rr.childResourceType,
              } as Location;
            });
          this.locationGroups.push({
            name: l.name,
            type: l.type,
            id: l.id,
            resources: locs,
          });
        }
      } else {
        if (
          !this.locationGroups.map((g) => g.type).includes(l.type) &&
          l.type != ResourceType.AcceleratorLocation
        ) {
          this.locationGroups.push({
            name: l.resourceType?.name,
            type: l.type,
            resources: this.filteredLocations.filter((x) => x.type == l.type),
          });
        }
      }
    });
    this.locationGroups = this.locationGroups.sort(
      (a, b) => (a.type ?? 0) - (b.type ?? 0)
    );
  }

  getChildLocations(_location: Location) { }

  removeLoc(location: Location): void {
    if (!this.checkRemove(location)) {
      const index = this.locations?.indexOf(location) ?? -1;
      if (index >= 0) {
        this.locations?.splice(index, 1);
        this.setValues();
        this.validateCheckboxes();
        this.loadLocations();
      }
      this.saveNotification();
    }
  }

  checkRemove(location: Location) {
    const parent = this.resourceRelations?.find(r => r.childResourceID == location.id)?.parentResource;
    const childResources = this.resourceRelations?.filter(r => r.parentResourceID === parent?.id).map(r => r.childResource);
    const tocas = this.tocas.filter(t => t.resourceTOCARelations?.map(b => b.resourceId).includes(location.id));
    // const shieldingMatch = childResources?.some(c => this.shieldings?.some(s => s.childResourceID === c?.id));
    const accShielding = this.shieldings.filter(s => s.locationsIDs);
    const otherShielding = this.shieldings.filter(s => !s.locationsIDs);
    const accShieldingMatch = accShielding?.some(a => {
      const ids = JSON.parse(a.locationsIDs || '[]'); // safely parse or default to empty array
      const sharedIds = JSON.parse(a.sharedWith?.[0] || '[]'); // safely parse or default to empty array
      return ids.includes(location.id) || sharedIds.includes(location.id);;
    });
    const otherShieldingMatch = childResources?.some(c => otherShielding?.some(s => s.childResourceID === c?.id));
    const restrictionMatch = this.scfMaster?.scfRestrictionsV2?.some(r => r.restriction?.resourceID == parent?.id || r.restriction?.resourceID == location.id);
    const tocaMatch = childResources?.some(_c => this.utils.intersect(tocas.map(t => t.id), this.shieldings.filter(s => s.childResourceType == ResourceType.TOCA).map(s => -(s.childResourceID ?? 0))));
    if (location.code == 'TopOff' && this.shieldings.some(s => s.childResourceType == ResourceType.TOCA)) return true;
    return accShieldingMatch || otherShieldingMatch || restrictionMatch || tocaMatch;
  }

  // Precomputed result of checkRemove() per location. The template reads this map
  // instead of calling checkRemove() directly: checkRemove's inputs (shieldings,
  // restrictions, resourceRelations) can change synchronously within a single
  // change-detection cycle (e.g. during selectedLoc), which made the tooltip
  // binding flip mid-cycle and throw NG0100. The map is only reassigned outside
  // the view-check (setValues/getValues/relations load), so it's stable per pass.
  removeBlockedMap = new Map<Location, boolean>();

  /** Recomputes removeBlockedMap from the current locations/shielding/restrictions. */
  updateRemoveBlocked(): void {
    const map = new Map<Location, boolean>();
    (this.locations ?? []).forEach((l) => map.set(l, this.checkRemove(l)));
    this.removeBlockedMap = map;
  }

  selectedLoc(event: MatAutocompleteSelectedEvent) {
    if (!this.locations) {
      this.locations = [];
    }
    if (
      !this.locations?.some((l) => l.id === event.option.value?.id) &&
      event.option.value !== null &&
      event.option.value !== undefined
    ) {
      // Emit new location to populate any missing restrictions
      setTimeout(() => {
        this.locationAdded.emit(event.option.value.id);
      }, 0);

      this.locations.push(event.option.value);
      this.setValues();
      this.validateCheckboxes();
      this.loadLocations();
      this.locationInput.nativeElement.value = '';
      this.locationsCtrl?.setValue(this.locations);
    }
    this.saveNotification();
  }

  addLoc(event: MatChipInputEvent) {
    if (!this.matAutocompleteLoc.isOpen) {
      const input = event.input;
      const value = event.value;
      if (input) {
        input.value = '';
      }
      if (value !== '') {
        const newLocation: Location = {
          id: 0,
          name: value,
          type: -1,
          newLocation: true,
        };
        this.locations?.push(newLocation);
        this.setValues();
      }
      this.locationsCtrl?.setValue(this.locations);
      this.loadLocations();
    }
  }

  onLocChange(code?: string) {
    if (!code?.includes('Arrow')) {
      this.loadLocations();
      this.filteredLocations = this.filteredLocations.filter((loc) =>
        loc.name?.toLowerCase().includes(
          String(this.locationsCtrl?.value || '')
            .trim()
            .toLowerCase()
        )
      );
      this.createLocationGroups();
    }
  }
  //#endregion

  //#region Checkboxes
  validateCheckboxes() {
    this.notiCheckBLCtrl?.setValue(
      this.locations?.some((x) => x.type === ResourceType.Beamline)
    );
  }
  //#endregion

  //#region Shieldings
  getShieldings(shieldingAffected: string) {
    const shld: ResourceRelation[] = [];
    if (shieldingAffected && shieldingAffected !== '') {
      const shieldings: ResourceRelation[] = JSON.parse(shieldingAffected);
      return shieldings.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    }
    return [];
  }

  /**
   * Rebuilds SCF shielding and TOCA mention options for the selected locations.
   * Also identifies existing TOCA mentions whose active BRATOI rows are unapproved.
   */
  loadRelatedShieldings() {
    // Existing unapproved TOCA mentions remain visible but are struck out in the description.
    this.unapprovedTocaMentionIds = this.bratoiService.getUnapprovedTocaMentionIds(this.bratois);
    this.allShieldings = [];
    this.locations?.map((location) => {
      if (location.type ?? 0 > 0) {
        let resource = this.resources?.find((x) => x.id == location.id);
        if (resource && resource.type == ResourceType.Branchline) {
          resource = this.resources.find(
            (r) =>
              resource?.childResources?.length &&
              r.id == resource.childResources[0]?.parentResourceID
          );
        }

        const shutterShieldings = this.resources
          ?.find(
            (r) =>
              r.id ==
              resource?.parentResources?.find(
                (x) => x.parentResourceType == ResourceType.Shutter
              )?.parentResourceID
          )
          ?.childResources?.filter(
            (x) => x.childResourceType == ResourceType.Shielding
          );

        if (shutterShieldings) {
          shutterShieldings.map((s) => {
            const parents = this.resourceRelations?.filter(
              (x) => x.childResourceID == s.childResourceID
            );
            s.shared = parents?.length > 1;
            s.childResourceType = ResourceType.Shielding;
            s.childResourceTypeName = 'Shielding';
          });
          this.allShieldings = this.allShieldings.concat(
            shutterShieldings.sort((a, b) =>
              utils.SortBeamlines(a.childResourceName, b.childResourceName)
            )
          );
        }

        const accShieldings = this.resources
          .find(
            (r) =>
              r.id ==
              resource?.parentResources?.find(
                (x) => x.parentResourceType == ResourceType.Accelerator
              )?.parentResourceID
          )
          ?.childResources?.filter(
            (x) => x.childResourceType == ResourceType.Shielding
          )
          .filter((x) =>
            (utils.JSONparse(x.locationsIDs) as number[]).includes(
              resource?.id ?? 0
            )
          );

        if (accShieldings) {
          accShieldings.map((s) => {
            const parents = this.resources.find(
              (r) => r.id == s.childResourceID
            )?.parentResources;
            s.shared = parents?.length ?? 0 > 1 ? true : false;
            s.sharedWith = parents
              ?.filter((rr) => rr.id != s.id)
              ?.map((rr) => rr.locationsIDs ?? '');
            s.childResourceType = ResourceType.Shielding;
            s.childResourceTypeName = ResourceType[ResourceType.Shielding];
          });
          this.allShieldings = this.allShieldings.concat(accShieldings);
        }

        if (resource?.type == ResourceType.AcceleratorLocation) {
          const locationShieldings = this.resourceRelations?.filter((r) =>
            (utils.JSONparse(r.locationsIDs) as number[])?.includes(
              resource?.id ?? 0
            )
          );
          locationShieldings?.map((x) => {
            if (!this.allShieldings.map((s) => s.id).includes(x.id)) {
              x.childResourceTypeName = 'Accelerator Location';
              x.childResourceType = ResourceType.AcceleratorLocation;
              this.allShieldings.push(x);
            }
          });
        }

        // Use the shared approval and catalog-fallback rules for SCF TOCA options.
        const tocas = this.bratoiService.resolveAvailableTocas(location, this.bratois, this.tocas);
        tocas.forEach((t) => {
          const toca = {
            childResourceID: -t.id,
            childResourceName: t.name,
            shared: true,
            position: 0,
            childResourceType: ResourceType.TOCA,
            childResourceTypeName: ResourceType[ResourceType.TOCA],
          } as ResourceRelation;
          if (
            !this.allShieldings
              .map((x) => x.childResourceID)
              .includes(toca.childResourceID)
          ) {
            this.allShieldings.push(toca);
          }
        });
      }
    });
    this.filterShieldings();
  }

  loadTocas() {
    this.tocas$ = this.store.select((state) => state.TOCA.data);
    this.tocasSubs = this.tocas$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      this.tocas = data;
    });
  }

  filterShieldings() {
    const uniqueShieldings = new Map();

    this.allShieldings.forEach(shielding => {
      if (!uniqueShieldings.has(shielding?.childResourceID)) {
        uniqueShieldings.set(shielding.childResourceID, shielding);
      }
    });

    this.filteredShieldings = Array.from(uniqueShieldings.values());
    this.createShieldingGroups();
  }

  createShieldingGroups() {
    this.shieldingGroups = [];
    this.filteredShieldings.map((l) => {
      if (l.childResourceType == ResourceType.Accelerator) {
        if (!this.shieldingGroups.map((g) => g.id).includes(l.id)) {
          const relations = this.resourceRelations?.filter(
            (rr) =>
              rr.parentResourceID == l.id &&
              !this.locations?.map((x) => x.id).includes(rr.childResourceID)
          );
          const shieldings = relations?.filter(
            (l) => l.childResourceType == ResourceType.AcceleratorLocation
          );
          this.shieldingGroups.push({
            name: l.childResourceName,
            type: l.childResourceType,
            id: l.id,
            shieldings,
          });
        }
      } else {
        if (
          !this.shieldingGroups
            .map((g) => g.type)
            .includes(l.childResourceType ?? 0) &&
          l.childResourceType != ResourceType.AcceleratorLocation
        ) {
          this.shieldingGroups.push({
            name: l.childResourceTypeName,
            type: l.childResourceType,
            shieldings: this.filteredShieldings.filter(
              (x) => x.childResourceType == l.childResourceType
            ),
          });
        }
      }
    });
    this.shieldingGroups = this.shieldingGroups.sort((a, b) => (a.type ?? 0) - (b.type ?? 0));
    this.getMentions();
  }

  //#region Mentions
  getMentions() {
    if (this.shieldingGroups) {
      this.mentions = this.shieldingGroups.flatMap(group => {
        // Create the group item with necessary properties
        const groupItem = {
          id: `group-${group.id || group.type}`,
          value: group.name,
          isGroup: true,
          parentResourceType: group.type,
          childResourceType: undefined,
          shared: false,
          sharedWith: [],
          disabled: true,
          title: ''
        };

        // Map the shieldings to include the necessary properties
        const shieldingItems = group.shieldings ? group.shieldings.map((shielding) => {
          const resource = this.resources.find(x => x.id === shielding.childResourceID);
          const links = resource?.windchillLinks;
          return {
            id: shielding.childResourceID,
            value: shielding.childResourceName,
            parentGroup: group.name,
            parentResourceType: shielding.parentResourceType,
            childResourceType: shielding.childResourceType,
            shared: shielding.shared,
            sharedWith: shielding.sharedWith,
            title: resource?.keText,
            windchillLinks: resource?.windchillLinks
          };
        }) : [];

        return [groupItem, ...shieldingItems]; // Combine the group and its shieldings
      });
    }
  }

  mentionsChanged(e: any) {
    this.selectedMentions = e;
    const selectedShielding = this.selectedMentions?.filter(m => m > 0);
    const selectedTocas = this.selectedMentions?.filter(m => m < 0);
    const addedShieldings = (selectedShielding || [])
      .map(mention =>
        this.allShieldings.find(r =>
          r.childResourceID === mention &&
          !this.shieldings.some(s => s.childResourceID === r.childResourceID)
        )
      )
      .filter((r): r is ResourceRelation => r !== undefined); // Type guard
    addedShieldings.forEach(option => this.selectedShl(option));

    // Handle removed shielding
    const removedShieldings = this.shieldings.filter(s => !selectedShielding?.includes(s.childResourceID));
    removedShieldings.forEach(shielding => this.removeShl(shielding));

    // Handle added TOCAs
    const addedTocas = (selectedTocas || [])
      .map(mention =>
        this.tocas.find(t =>
          t.id === -mention &&
          !this.shieldings.some(s => s.childResourceID === t.id)  // Exclude if it's in shieldings
        )
      )
      .filter((t): t is TOCA => t !== undefined); // Type guard to remove undefined

    addedTocas.forEach(a => {
      const addedResourceRelation = {
        childResourceID: -a.id,
        childResourceType: this.resourceTypeEnum.TOCA,
        childResourceName: a.name
      } as ResourceRelation;
      this.selectedShl(addedResourceRelation);
    });

    // Handle removed TOCAs
    const removedTocas = this.shieldings.filter(s => !selectedTocas?.includes(s.childResourceID)).filter(m => m.childResourceType == this.resourceTypeEnum.TOCA);
    removedTocas.forEach(shielding => this.removeShl(shielding));
  }
  //#endregion


  onShlChange(code?: string) {
    if (!code?.includes('Arrow')) {
      this.loadRelatedShieldings();
      this.filteredShieldings = this.filteredShieldings.filter((s) =>
        s.childResourceName
          ?.toLowerCase()
          .includes(this.shieldingsCtrl?.value?.trim()?.toLowerCase())
      );
      this.createShieldingGroups();
    }
  }

  addShl(event: any): void {
    const input = event.input;
    if (!this.matAutocompleteShl.isOpen) {
      const value = this.shieldingsCtrl?.value;
      // Add our location
      if (value && typeof value == 'string') {
        let s = undefined;
        const found = this.allShieldings.find(
          (sh) =>
            sh.childResourceName?.trim()?.toLowerCase() ==
            value?.trim()?.toLowerCase()
        );
        if (found) {
          s = found;
          this.selectedShl(found);
        } else {
          s = {
            id: 0,
            parentResourceID: 0,
            parentResource: null,
            parentResourceName: 'manualEntry',
            childResourceID: 0,
            childResourceName: value,
          };
          this.shieldings.push(s);
          this.orderShieldings();
          this.setValues();
        }
      }
      // Reset the input value
      if (input) {
        input.value = '';
      }
      this.shieldingsCtrl?.setValue(null);
      this.loadRelatedShieldings();
    }
  }

  selectedShl(option: ResourceRelation) {
    if (!this.shieldings) this.shieldings = [];
    if (!this.shieldings.includes(option)) {
      this.shieldings.push(option);
      this.orderShieldings();
      this.setValues();
      this.shieldingInput.nativeElement.value = '';
      this.shieldingsCtrl?.setValue(null);
      if (option.childResourceType === this.resourceTypeEnum.TOCA) {
        this.checkTocaMovCtrl?.setValue(true);
        this.setValues();
        const tocaId = -(option?.childResourceID ?? 0);
        // Resolve shared TOCA locations consistently with RSF and add each location only once.
        const resources = this.bratoiService.resolveRelatedResources(tocaId, this.bratois, this.tocas, this.resources);
        resources.forEach((r) => {
          const loc = { autoLocation: true, id: r.id, name: r.name, type: r.type } as Location;
          const matOption: MatAutocompleteSelectedEvent = JSON.parse(JSON.stringify({ option: { value: loc, }, }));
          this.alert.message('SCF_NewAddedLocation');
          this.selectedLoc(matOption);
        });
        const topOff = this.resources.find(
          (r) => r.code == 'TopOff'
        ) as Location;
        topOff.autoLocation = true;
        const matOption: MatAutocompleteSelectedEvent = JSON.parse(
          JSON.stringify({ option: { value: topOff } })
        );
        this.selectedLoc(matOption);
      } else {
        var shutters = this.resourceRelations
          .filter(
            (rr) =>
              rr.childResourceID == option.childResourceID &&
              rr.parentResourceType == ResourceType.Shutter
          )
          .map((rr) => rr.parentResource);
        var beamlineRR = this.resourceRelations.filter(
          (rr) =>
            rr.childResourceType == ResourceType.Beamline &&
            shutters.map((s) => s?.id).includes(rr.parentResourceID)
        );
        var locations = beamlineRR.map((rr) => rr.childResource) as Location[];
        if (!locations?.length && option.locationsIDs) {
          const locationsIDs = utils.JSONparse(option.locationsIDs) as number[];
          var locations = this.resources.filter((x) =>
            locationsIDs.includes(x.id ?? 0)
          ) as Location[];

          if (option.shared) {
            let locationsIDs: any[] = [];
            option.sharedWith?.map((sw) => {
              const IDs = utils.JSONparse(sw);
              locationsIDs = locationsIDs?.concat(IDs);
            });
            locationsIDs = [...new Set(locationsIDs)];
            const locationsShared = this.resources
              .filter((x) => locationsIDs.includes(x.id))
              .map((r) => {
                return {
                  id: r.id,
                  name: r.name,
                  type: r.type,
                  status: r.status,
                  description: r.description,
                  resourceTypeID: r.resourceTypeID,
                  resourceType: r.resourceType,
                } as Location;
              });
            locations = locations.concat(locationsShared);
          }
        }

        locations?.map((loc) => {
          loc.autoLocation = true;
          const matOption: MatAutocompleteSelectedEvent = JSON.parse(
            JSON.stringify({ option: { value: loc } })
          );
          if (!this.locations?.some((l) => l.id == loc.id)) {
            this.alert.message(
              'SCF_NewAddedLocation',
              [],
              undefined,
              undefined,
              10000
            );
            this.selectedLoc(matOption);
          }
        });

        this.setValues();
      }
    }
  }

  removeShl(shielding: ResourceRelation): void {
    const index = this.shieldings.indexOf(shielding);
    if (index >= 0) {
      this.shieldings.splice(index, 1);
      if (!this.shieldings.filter((x) => x.childResourceType === 0).length) {
        // TOCA
        this.checkTocaMovCtrl?.setValue(false);
      }
      this.orderShieldings();
      this.setValues();
    }
    this.loadRelatedShieldings();
  }

  drop(event: CdkDragDrop<ResourceRelation[]>) {
    moveItemInArray(
      this.shieldings,
      event.item.data.position,
      event.currentIndex
    );
    this.orderShieldings();
    this.formGroup.markAsDirty();
  }

  dropListDropped(_event: CdkDragDrop<ResourceRelation[]>) {
    if (this.sourceIndex != this.targetIndex && this.targetIndex > -1) {
      moveItemInArray(this.shieldings, this.sourceIndex, this.targetIndex);
      this.sourceIndex = this.targetIndex;
      this.orderShieldings();
      this.setValues();
    }
  }

  dragMoved(e: CdkDragMove) {
    const point = this.getPointerPositionOnPage(e.event);
    const drag = e.source.element;
    this.sourceIndex = e.source.data.position;
    let index = 0;

    this.placeholder.element.nativeElement.firstChild?.childNodes.forEach(
      (node: any) => {
        if (node.nodeName != '#comment') {
          if (
            __isInsideDropListClientRect(node, point.x, point.y) &&
            index != this.sourceIndex
          ) {
            this.targetIndex = index;
          }
          index++;
        }
      }
    );
  }

  getPointerPositionOnPage(event: MouseEvent | TouchEvent) {
    // `touches` will be empty for start/end events so we have to fall back to `changedTouches`.
    const point = __isTouchEvent(event)
      ? event.touches[0] || event.changedTouches[0]
      : event;
    const scrollPosition = this.viewportRuler.getViewportScrollPosition();

    return {
      x: point.pageX - scrollPosition.left,
      y: point.pageY - scrollPosition.top,
    };
  }

  orderShieldings() {
    let i = 0;
    this.shieldings.map((s) => {
      s.position = i;
      i++;
    });
  }

  // trackBy helpers for the autocomplete option lists (rebuilt on every keystroke)
  // so Angular reuses option DOM instead of tearing it all down each time.
  // (item typed `any` so each is assignable to the list's TrackByFunction<T>.)
  trackByGroupId = (_: number, g: any) => g?.id ?? g?.type;
  trackByResourceId = (_: number, r: any) => r?.id;
  trackByShieldingId = (_: number, s: any) => s?.childResourceID;
  trackByUserId = (_: number, u: any) => u?.id;

  shared(shielding: ResourceRelation) {
    if (shielding.parentResourceType == this.resourceTypeEnum.Shutter)
      return shielding.shared;
    else return shielding.sharedWith?.length ?? 0 > 0;
  }

  //#endregion

  //#region Bratoi
  loadBratoi() {
    this.bratois$ = this.store.select((state) => state.Bratoi.data);
    this.bratoisSubs = this.bratois$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data?.length > 0) {
        this.bratois = data
          .sort((a, b) =>
            utils.SortBeamlines(a.resourceString, b.resourceString)
          )
          .filter((bl) => bl.isActive);
        // Refresh saved mention presentation after BRATOI approval data arrives.
        if (this.resources?.length) {
          this.loadRelatedShieldings();
        }
      }
    });
  }
  //#endregion

  //#region Related Links
  addRelatedLink() {
    if (
      !this.disabled &&
      this.status &&
      (this.status.isCreating ||
        this.status.isEditing ||
        this.status.isCreatingAmendment ||
        this.status.isEditingAmendment)
    ) {
      const dialogRef = this.dialog.open(AddRelatedLinkComponent, {
        maxWidth: '800px',
        data: {
          obj: {
            Text: null,
            URL: null,
          } as RelatedLink,
        },
      });
      dialogRef.afterClosed().subscribe((result: RelatedLink) => {
        if (result) {
          this.relatedLinksCtrl?.markAsDirty();
          this.relatedLinks.push({
            text: result.Text,
            url: result.URL,
            formID: 1,
            id: 0,
          } as RelatedLinkBase);
          this.setValues();
        }
      });
    }
  }

  removeRelatedLink(i: number) {
    this.relatedLinks.splice(i, 1);
    this.relatedLinksCtrl?.markAsDirty();
    this.setValues();
  }

  //#endregion

  //#region Documents

  removeDoc(document: RelatedDocument): void {
    const index = this.documents?.indexOf(document) ?? -1;

    if (index >= 0) {
      this.documents?.splice(index, 1);
    }
    this.setValues();
  }

  openDocument(e: RelatedDocument) {
    window.location.href = '#/redirect/' + e.relatedDocumentSerialNo;
  }
  //#region Notification

  setNotificationChecks(workRequired: WorkRequired = 0, e: any) {
    if (this.scfMaster && !this.status?.isViewingAmendment) {
      this.scfMaster.notifyHP = true;
      this.scfMaster.notifyBLSci =
        this.scfMaster.notifyBLSci ||
        this.locations?.some((l) => l.type === ResourceType.Beamline);
      this.scfMaster.notifyBRC =
        this.scfMaster.notifyBRC || this.checkPermBlCtrl?.value;
      this.scfMaster.notifyARC =
        this.scfMaster.notifyARC || this.checkPermAccCtrl?.value;
      this.scfMaster.notifyRP =
        this.scfMaster.notifyRP ||
        this.checkPermAccCtrl?.value ||
        this.checkPermBlCtrl?.value;
      this.scfMaster.notifySnA =
        this.scfMaster.notifySnA || this.checkTocaMovCtrl?.value;
      this.notiCheckHPCtrl?.setValue(this.scfMaster?.notifyHP);
      this.notiCheckBLCtrl?.setValue(this.scfMaster?.notifyBLSci);
      this.notiCheckBRCCtrl?.setValue(this.scfMaster?.notifyBRC);
      this.notiCheckRFCtrl?.setValue(this.scfMaster?.notifyRF);
      this.notiCheckSACtrl?.setValue(this.scfMaster?.notifySnA);
      this.notiCheckARCtrl?.setValue(this.scfMaster?.notifyARC);
      this.notiCheckRPCtrl?.setValue(this.scfMaster?.notifyRP);
    } else if (this.status?.isViewingAmendment) {
      if (this.scfAmendment) {
        this.scfAmendment.notifyHP = true;
        this.scfAmendment.notifyBLSci =
          this.scfAmendment.notifyBLSci ||
          (this.locations?.some((l) => l.type === ResourceType.Beamline) ??
            false);
        this.scfAmendment.notifyBRC =
          this.scfAmendment.notifyBRC || this.checkPermBlCtrl?.value;
        this.scfAmendment.notifyARC =
          this.scfAmendment.notifyARC || this.checkPermAccCtrl?.value;
        this.scfAmendment.notifyRP =
          this.scfAmendment.notifyRP ||
          this.checkPermAccCtrl?.value ||
          this.checkPermBlCtrl?.value;
        this.scfAmendment.notifySnA =
          this.scfAmendment.notifySnA || this.checkTocaMovCtrl?.value;
        this.notiCheckHPCtrl?.setValue(this.scfAmendment.notifyHP);
        this.notiCheckBLCtrl?.setValue(this.scfAmendment.notifyBLSci);
        this.notiCheckBRCCtrl?.setValue(this.scfAmendment.notifyBRC);
        this.notiCheckRFCtrl?.setValue(this.scfAmendment.notifyRF);
        this.notiCheckSACtrl?.setValue(this.scfAmendment.notifySnA);
        this.notiCheckARCtrl?.setValue(this.scfAmendment.notifyARC);
        this.notiCheckRPCtrl?.setValue(this.scfAmendment.notifyRP);
      }
    }
    const scfWorkRequired: SCFWorkRequired = {
      workRequired,
      checked: e.checked,
    };
    if (workRequired) {
      this.setValues();
      this.workRequiredChanged.emit(scfWorkRequired);
    }
  }

  saveNotification() {
    this.setValues();
  }
  //#endregion

  selectedRequester(_e: any) {
    this.setValues();
  }

  descriptionChanged(e: any) {
    this.descriptionCtrlText = e;
    this.setValues();
  }

  changedDate(e: { value: string | number | Date; }) {
    const estimatedStartOn = new Date(e.value);
    const estimatedEndOn = new Date(this.estimatedEndCtrl?.value);
    if (moment(estimatedEndOn).isBefore(moment(estimatedStartOn))) {
      this.estimatedEndCtrl?.setValue(estimatedStartOn);
    }
    this.setValues();
  }

  async editOlog() {
    if (this.scfMaster?.id) {
      try {
        const data = await this.ologService
          .getOlog(
            FormType.SCF,
            this.scfMaster.id,
            OlogNotificationType.Activate
          )
          .toPromise();
        if (data) {
          const dialogRef = this.dialog.open(PartialOlogComponent, {
            height: 'fit-content',
            width: '80%',
            data,
          });
          dialogRef.afterClosed().subscribe((isSaved) => {
            console.log('The dialog was closed');
            if (isSaved) {
              console.log(isSaved);
            }
          });
        }
      } catch (error) {
        console.log(error);
      }
    }
  }

  async editNotification() {
    await this.getNotification();
    if (this.notificationMessage) {
      this.notificationService
        .getNotification(
          NotificationType.CreateSCF,
          this.scfMaster?.id ?? 0,
          this.locations ?? []
        )
        .toPromise()
        .then(async (res: any) => {
          if (res) {
            this.notificationMessage = res;
            if (this.scfMaster) {
              const emails = await this.scfService.getEmails(this.scfMaster);
              this.openNotificationDialog(res, emails, this.disableControls);
            }
          }
        });
    } else {
      if (this.scfMaster) {
        const emails = await this.scfService.getEmails(this.scfMaster);
        const notification = {
          notificationId: NotificationType.CreateSCF,
          documentId: this.scfMaster?.id,
          toAddresses: this.utils.JSONstringify({
            computed: emails,
            added: [],
          }),
          type: 5,
          locations: this.locations ?? [],
        } as NotificationMessage;
        this.notificationService
          .createNotification(notification)
          .toPromise()
          .then((res: any) => {
            this.openNotificationDialog(res, emails, this.disableControls);
          },
            (error: any) => {
              console.log(error);
            }
          );
      }
    }
  }

  getNotification() {
    return new Promise((resolve, reject) => {
      this.notificationMessage = null;
      if (this.scfMaster?.id) {
        this.notificationService
          .getNotification(
            NotificationType.CreateSCF,
            this.scfMaster?.id,
            this.locations ?? []
          )
          .toPromise()
          .then(
            (data: any) => {
              if (data) {
                this.notificationMessage = data;
              }
              if (this.disableControls) {
                this.notificationButtonLabel = 'View Notification';
                this.notificationButtonDisabled = false;
              } else {
                this.notificationButtonDisabled = false;
                this.notificationButtonLabel = 'Edit Notification';
              }
              resolve(true);
            },
            (error: any) => {
              console.log(error);
              reject(false);
            }
          );
      }
    });
  }

  openNotificationDialog(
    data: NotificationMessage,
    emailAddresses?: string[],
    readOnly?: boolean
  ): void {
    const dialogRef = this.dialog.open(NotificationComponent, {
      height: 'fit-content',
      width: '80%',
      data: {
        message: data,
        emailAddresses,
        readOnly,
      },
    });

    dialogRef
      .afterClosed()
      .toPromise()
      .then(() => {
        this.getNotification();
      });
  }

  //#region Differences

  diffWorkRequired() {
    return (
      utils.isNull(this.checkAccRFCtrl?.value, false) !==
      utils.isNull(this.scfMaster?.accRFShielding, false) ||
      utils.isNull(this.checkBlShCtrl?.value, false) !==
      utils.isNull(this.scfMaster?.blShielding, false) ||
      utils.isNull(this.checkPermAccCtrl?.value, false) !==
      utils.isNull(this.scfMaster?.permanentAccRemoval, false) ||
      utils.isNull(this.checkPermBlCtrl?.value, false) !==
      utils.isNull(this.scfMaster?.permanentBLRemoval, false) ||
      utils.isNull(this.checkTocaMovCtrl?.value, false) !==
      utils.isNull(this.scfMaster?.tocaMovement, false)
    );
  }

  diffLocations() {
    // All diff* methods sort copies only: these live arrays drive the visible
    // list order, and an in-place sort here reshuffles the display.
    const current = utils
      .JSONstringify(
        this.locations
          ?.slice()
          .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
          .map((l) => {
            return { id: l.id, name: l.name } as Location;
          })
      )
      ?.replace(/\s/g, '');
    const original = utils
      .JSONstringify(
        this.scfMaster?.locations
          ?.slice()
          .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
          .map((l) => {
            return { id: l.id, name: l.name } as Location;
          })
      )
      ?.replace(/\s/g, '');
    return current !== original;
  }

  diffDocuments() {
    const current = utils
      .JSONstringify(
        this.documents
          ?.slice()
          .sort((a, b) =>
            utils.sort(
              a.relatedDocumentSerialNo,
              b.relatedDocumentSerialNo,
              true
            )
          )
          .map((l) => {
            return {
              relatedDocumentId: l.relatedDocumentId,
              relatedDocumentSerialNo: l.relatedDocumentSerialNo,
            } as RelatedDocument;
          })
      )
      ?.replace(/\s/g, '');
    const original = utils
      .JSONstringify(
        this.scfMaster?.relatedDocuments
          ?.slice()
          .sort((a, b) =>
            utils.sort(
              a.relatedDocumentSerialNo,
              b.relatedDocumentSerialNo,
              true
            )
          )
          .map((l) => {
            return {
              relatedDocumentId: l.relatedDocumentId,
              relatedDocumentSerialNo: l.relatedDocumentSerialNo,
            } as RelatedDocument;
          })
      )
      ?.replace(/\s/g, '');
    return current != original;
  }

  diffRelatedLinks() {
    const current = utils
      .JSONstringify(
        this.relatedLinks?.slice().sort((a, b) => utils.sort(a.text, b.text, true))
      )
      .replace(/\s/g, '');
    if (this.scfMaster?.relatedLinks) {
      const original = utils
        .JSONstringify(
          this.getRelatedLinks(this.scfMaster.relatedLinks)?.sort(
            (a: any, b: any) => utils.sort(a.Text, b.Text, true)
          )
        )
        .replace(/\s/g, '');
      return current != original;
    }
    return false;
  }

  diffShieldings() {
    const current = utils
      .JSONstringify(
        this.shieldings
          ?.slice()
          .sort((a, b) =>
            utils.sort(a.childResourceID, b.childResourceID, true)
          )
          .map((l) => {
            return {
              childResourceID: l.childResourceID,
              childResourceName: l.childResourceName,
            } as ResourceRelation;
          })
      )
      .replace(/\s/g, '');
    if (this.scfMaster?.shieldingAffected) {
      const original = utils
        .JSONstringify(
          this.getShieldings(this.scfMaster.shieldingAffected)
            ?.sort((a, b) =>
              utils.sort(a.childResourceID, b.childResourceID, true)
            )
            .map((l) => {
              return {
                childResourceID: l.childResourceID,
                childResourceName: l.childResourceName,
              } as ResourceRelation;
            })
        )
        .replace(/\s/g, '');
      return current != original;
    }
    return false;
  }

  diffNotificationChecks() {
    return (
      utils.isNull(this.notiCheckARCtrl?.value, false) !==
      utils.isNull(this.scfMaster?.notifyARC, false) ||
      utils.isNull(this.notiCheckBLCtrl?.value, false) !==
      utils.isNull(this.scfMaster?.notifyBLSci, false) ||
      utils.isNull(this.notiCheckBRCCtrl?.value, false) !==
      utils.isNull(this.scfMaster?.notifyBRC, false) ||
      utils.isNull(this.notiCheckHPCtrl?.value, false) !==
      utils.isNull(this.scfMaster?.notifyHP, false) ||
      utils.isNull(this.notiCheckRFCtrl?.value, false) !==
      utils.isNull(this.scfMaster?.notifyRF, false) ||
      utils.isNull(this.notiCheckSACtrl?.value, false) !==
      utils.isNull(this.scfMaster?.notifySnA, false) ||
      utils.isNull(this.notiCheckRPCtrl?.value, false) !==
      utils.isNull(this.scfMaster?.notifyRP, false)
    );
  }
  /////

  formValid() {
    if (
      this.scfMaster &&
      this.status &&
      (this.status.isCreating ||
        this.status.isCreatingAmendment ||
        this.status.isEditing ||
        this.status.isEditingAmendment)
    ) {
      if (this.locations?.length == 0) {
        this.locationsCtrl?.setErrors({ required: true });
        this.chipListLoc.errorState = true;
      } else {
        this.locationsCtrl?.setErrors(null);
        this.chipListLoc.errorState = false;
      }
      if (this.shieldings?.length == 0 && !this.hideShielding()) {
        this.shieldingsCtrl?.setErrors({ required: true });
        this.chipListShl.errorState = true;
      } else {
        this.shieldingsCtrl?.setErrors(null);
        this.chipListShl.errorState = false;
      }
      if (!this.requesterCtrl?.value) {
        this.requesterCtrl?.setErrors({ required: true });
        this.requesterCtrl?.markAsTouched();
      }
      const estimatedInitOn = moment(this.estimatedInitCtrl?.value);
      const estimatedEndOn = moment(this.estimatedInitCtrl?.value);
      const today = moment(this.today).date();
      if (
        !estimatedInitOn.isValid() ||
        estimatedInitOn.isBefore(moment(today))
      ) {
        this.estimatedInitCtrl?.setErrors({ invalidDate: true });
        this.estimatedInitCtrl?.markAsTouched();
      } else {
        this.estimatedInitCtrl?.setErrors(null);
      }
      if (
        !estimatedEndOn.isValid() ||
        estimatedEndOn.isBefore(estimatedInitOn)
      ) {
        this.estimatedEndCtrl?.setErrors({ invalidDate: true });
        this.estimatedEndCtrl?.markAsTouched();
      } else {
        this.estimatedEndCtrl?.setErrors(null);
      }
      if (
        (!this.status?.isViewingAmendment && !this.scfMaster?.description) ||
        (this.status?.isViewingAmendment && !this.scfAmendment?.description)
      ) {
        this.descriptionCtrl.setErrors({ required: true });
        this.descriptionHasError = true;
      } else {
        this.descriptionCtrl.setErrors(null);
        this.descriptionHasError = false;
      }
      if (
        !this.checkAccRFCtrl?.value &&
        !this.checkBlShCtrl?.value &&
        !this.checkPermAccCtrl?.value &&
        !this.checkPermBlCtrl?.value &&
        !this.checkTocaMovCtrl?.value
      ) {
        this.inputWorkHiddenCtrl?.setErrors({ check: true });
        this.inputWorkHiddenCtrl?.markAsTouched();
      } else {
        this.inputWorkHiddenCtrl?.setErrors(null);
      }
    } else {
      this.locationsCtrl?.setErrors(null);
      if (this.chipListLoc) {
        this.chipListLoc.errorState = false;
      }
      this.shieldingsCtrl?.setErrors(null);
      if (this.chipListShl) {
        this.chipListShl.errorState = false;
      }
      this.descriptionCtrl.setErrors(null);
      this.descriptionHasError = false;
    }
    // Cache the result. Templates must read isValid() (the cached value) rather
    // than calling formValid() — formValid() has side effects (setErrors,
    // markAsTouched, descriptionHasError) and calling it during change detection
    // mutates bound state mid-cycle -> NG0100.
    this.formValidValue = this.formGroup.valid && !this.descriptionHasError;
    return this.formValidValue;
  }

  /** Side-effect-free validity for template bindings (see formValid). */
  isValid(): boolean {
    return this.formValidValue;
  }

  hideShielding() {
    // Hide shielding field for new SCFs
    return this.scfMaster?.createdOn && moment(this.scfMaster?.createdOn).isAfter(moment('2026-02-28T08:00:00Z'));
  }
}

function __isTouchEvent(event: MouseEvent | TouchEvent): event is TouchEvent {
  return event.type.startsWith('touch');
}

function __isInsideDropListClientRect(node: any, x: number, y: number) {
  const { top, bottom, left, right } = node.getBoundingClientRect();
  return y >= top && y <= bottom && x >= left && x <= right;
}

function __indexOf(collection: { children: any; }, node: any) {
  return Array.prototype.indexOf.call(collection.children, node);
}
